import { useState } from "react";

const MAX_PROMO_PHOTOS = 10;

function BusinessMediaSection({
  businessLogoPreview,
  handleBusinessLogoSelection,
  setBusinessLogoPreview,

  agentProfilePreview,
  handleAgentProfileSelection,
  setAgentProfilePreview,

  promoImagePreviews,
  setPromoImagePreviews,
  setPromoImageFiles,
  onDeletePromoImage,
}) {
  const [selectedPromoIndex, setSelectedPromoIndex] = useState(0);
  const [promoUploadMessage, setPromoUploadMessage] = useState("");
  const remainingPromoSlots = Math.max(
    0,
    MAX_PROMO_PHOTOS - promoImagePreviews.length,
  );

  /* =========================
      PROMO IMAGE UPLOAD
  ========================= */

  const handlePromoImages = (e) => {
    const files = Array.from(e.target.files);

    if (!files.length) return;

    const acceptedFiles = files.slice(0, remainingPromoSlots);
    setPromoUploadMessage(
      files.length > remainingPromoSlots
        ? remainingPromoSlots > 0
          ? `Only ${remainingPromoSlots} more promotional photo${remainingPromoSlots === 1 ? "" : "s"} can be added. Maximum: ${MAX_PROMO_PHOTOS}.`
          : `Maximum ${MAX_PROMO_PHOTOS} promotional photos reached.`
        : "",
    );
    e.target.value = "";

    if (!acceptedFiles.length) return;

    /* STORE ORIGINAL FILES */

    setPromoImageFiles((prev) => [...prev, ...acceptedFiles]);

    /* CREATE PREVIEW OBJECTS */

    const previewImages = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    /* STORE PREVIEW IMAGES */

    setPromoImagePreviews((prev) => [...prev, ...previewImages]);
  };

  /* =========================
      REMOVE PROMO IMAGE
  ========================= */

  const removePromoImage = async (index) => {
    const image = promoImagePreviews[index];

    // Existing image from server
    if (!image.file && onDeletePromoImage) {
      const actualUrl = image.preview?.split("?")[0];
      console.log(actualUrl);

      const success = await onDeletePromoImage(actualUrl);

      if (!success) return;
    }

    const updatedImages = promoImagePreviews.filter((_, i) => i !== index);

    setPromoImagePreviews(updatedImages);

    // Remove newly selected file from upload list
    if (image.file) {
      setPromoImageFiles((prev) => prev.filter((file) => file !== image.file));
    }

    if (selectedPromoIndex >= updatedImages.length) {
      setSelectedPromoIndex(Math.max(updatedImages.length - 1, 0));
    }
  };

  return (
    <>
      {/* =========================
          BUSINESS LOGO
      ========================= */}

      <div className="business-media-card">
        <div className="media-title-section">
          <h2>Business Logo</h2>
          <p>optional</p>
        </div>

        <div className="business-logo-upload-wrapper">
          <label htmlFor="businessLogo" className="business-logo-upload">
            {businessLogoPreview ? (
              <>
                <img
                  src={businessLogoPreview}
                  alt=""
                  className="business-logo-preview"
                />

                <div
                  className="media-remove-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBusinessLogoPreview("");
                  }}
                >
                  ✕
                </div>
              </>
            ) : (
              <div className="empty-upload-placeholder">
                <div className="upload-plus-icon">+</div>
                <p>Upload image</p>
              </div>
            )}
          </label>

          <input
            id="businessLogo"
            type="file"
            accept="image/*"
            onChange={handleBusinessLogoSelection}
            className="hidden-input"
          />
        </div>
      </div>

      {/* =========================
          PROMOTIONAL PHOTOS
      ========================= */}

      <div className="business-media-card">
        <div className="media-title-section">
          <h2>Promotional Photos</h2>
          <p>optional</p>
        </div>
        <p className="promo-photo-count" aria-live="polite">
          {promoImagePreviews.length} / {MAX_PROMO_PHOTOS} photos
        </p>
        {promoUploadMessage && (
          <p className="promo-photo-limit-message" role="status">
            {promoUploadMessage}
          </p>
        )}

        {promoImagePreviews.length === 0 ? (
          <div className="promotional-upload-box">
            <label htmlFor="promoPhotos" className="promo-upload-label">
              <div className="promo-upload-inner">
                <div className="promo-upload-icon">↑</div>

                <h3>Upload Drop Images</h3>

                <p>Recommended image size: 750 px</p>
              </div>
            </label>

            <input
              id="promoPhotos"
              type="file"
              multiple
              accept="image/*"
              className="hidden-input"
              disabled={remainingPromoSlots === 0}
              onChange={handlePromoImages}
            />
          </div>
        ) : (
          <>
            {/* MAIN IMAGE */}

            <div className="promo-preview-wrapper">
              <button
                type="button"
                className="promo-arrow left"
                onClick={() =>
                  setSelectedPromoIndex(
                    selectedPromoIndex > 0 ? selectedPromoIndex - 1 : 0,
                  )
                }
              >
                ‹
              </button>

              <img
                src={promoImagePreviews[selectedPromoIndex]?.preview}
                alt=""
                className="promo-main-image"
              />

              <div
                className="promo-main-remove"
                onClick={() => removePromoImage(selectedPromoIndex)}
              >
                ✕
              </div>

              <button
                type="button"
                className="promo-arrow right"
                onClick={() =>
                  setSelectedPromoIndex(
                    selectedPromoIndex < promoImagePreviews.length - 1
                      ? selectedPromoIndex + 1
                      : promoImagePreviews.length - 1,
                  )
                }
              >
                ›
              </button>
            </div>

            {/* THUMBNAILS */}

            <div className="promo-thumbnails-row">
              {promoImagePreviews.map((img, index) => (
                <div key={index} className="promo-thumb-wrapper">
                  <div
                    className="promo-remove-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePromoImage(index);
                    }}
                  >
                    ✕
                  </div>

                  <img
                    src={img.preview}
                    alt=""
                    className={`promo-thumb ${
                      selectedPromoIndex === index ? "active-thumb" : ""
                    }`}
                    onClick={() => setSelectedPromoIndex(index)}
                  />
                </div>
              ))}

              {remainingPromoSlots > 0 && (
                <label htmlFor="promoPhotos" className="promo-add-more">
                  +
                </label>
              )}

              <input
                id="promoPhotos"
                type="file"
                multiple
                accept="image/*"
                className="hidden-input"
                disabled={remainingPromoSlots === 0}
                onChange={handlePromoImages}
              />
            </div>
          </>
        )}
      </div>

      {/* =========================
          AGENT PROFILE
      ========================= */}

      <div className="business-media-card">
        <div className="media-title-section">
          <h2>Agent Profile</h2>
        </div>

        <div className="agent-profile-upload-section">
          <p className="agent-photo-label">Profile Photo</p>

          <label htmlFor="agentProfile" className="business-logo-upload">
            {agentProfilePreview ? (
              <>
                <img
                  src={agentProfilePreview}
                  alt=""
                  className="business-logo-preview"
                />

                <div
                  className="media-remove-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAgentProfilePreview("");
                  }}
                >
                  ✕
                </div>
              </>
            ) : (
              <div className="empty-upload-placeholder">
                <div className="upload-plus-icon">+</div>
                <p>Upload image</p>
              </div>
            )}
          </label>

          <input
            id="agentProfile"
            type="file"
            accept="image/*"
            onChange={handleAgentProfileSelection}
            className="hidden-input"
          />
        </div>
      </div>
    </>
  );
}

export default BusinessMediaSection;
